import { useState } from 'react';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import { adSettingsApi } from '../../api/adSettingsApi';
import { useCanEditAdSettings } from '../../hooks';
import { brandTermsInput } from '../../utils/adSettingsForm';
import { useAdSettings } from './useAdSettings';
import styles from './PaidSettings.module.css';

/** The words that make a search one for the practice by name: the ads report splits brand from the rest with them. */
export function BrandTermsCard({ locationId }: { locationId: string }) {
  const toast = useToast();
  const canEdit = useCanEditAdSettings();
  const { data, isLoading, save } = useAdSettings(locationId);
  const [draft, setDraft] = useState<string | null>(null);

  if (isLoading || !data)
    return (
      <Card title="Brand terms">
        <Spinner size="sm" />
      </Card>
    );

  const usingDefaults = data.brandTerms.length === 0;
  const shown = usingDefaults ? data.defaultBrandTerms : data.brandTerms;
  const submit = () => {
    const result = brandTermsInput(draft ?? '');
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    save.mutate(() => adSettingsApi.saveBrandTerms(locationId, result.input), {
      onSuccess: () => setDraft(null),
    });
  };

  return (
    <Card title="Brand terms">
      <p className={styles.intro}>
        Searches with these words count as people looking for your practice by name.
      </p>
      {draft === null ? (
        <>
          <div className={styles.chips}>
            {shown.length === 0 ? (
              <span className={styles.hint}>None.</span>
            ) : (
              shown.map((t) => (
                <span key={t} className={styles.chip}>
                  {t}
                </span>
              ))
            )}
          </div>
          {usingDefaults && (
            <p className={styles.hint}>
              The defaults, from your practice name, website and providers. Edit to set your own.
            </p>
          )}
          {canEdit && (
            <div className={styles.actions}>
              <Button variant="secondary" size="sm" onClick={() => setDraft(shown.join('\n'))}>
                Edit terms
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <label className={styles.label} htmlFor={`brand-terms-${locationId}`}>
            One per line. Leave empty to use the defaults.
          </label>
          <textarea
            id={`brand-terms-${locationId}`}
            className={styles.terms}
            rows={5}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={submit} disabled={save.isPending}>
              Save terms
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
